import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BookOpen,
  Sparkles,
  User,
  CheckCircle2,
  ShieldCheck,
  Globe,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';
import { renderAISummary as defaultRenderAISummary } from '../utils';

const SharedArticleView = ({ renderAISummary = defaultRenderAISummary }) => {
  const { id } = useParams();
  const { language, setLanguage, languages } = useLanguage();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchSharedArticle = async () => {
      if (!id) {
        if (isMounted) {
          setNotFound(true);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setNotFound(false);

      try {
        let { data, error } = await supabase
          .from('daily_matches')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (error) {
          console.warn('Could not fetch from daily_matches:', error.message);
        }

        if (!data) {
          const { data: savedData, error: savedError } = await supabase
            .from('saved_articles')
            .select('*')
            .eq('id', id)
            .maybeSingle();

          if (savedError) {
            console.warn('Could not fetch from saved_articles:', savedError.message);
          }

          data = savedData;
        }

        if (isMounted) {
          if (data) {
            setArticle(data);
          } else {
            setNotFound(true);
          }
        }
      } catch (err) {
        console.error('Unexpected error fetching shared article:', err);
        if (isMounted) {
          setNotFound(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSharedArticle();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const title = article?.article_title || article?.title;
  const date = article?.article_date || article?.date;
  const originalUrl = article?.article_url || article?.doi || article?.url;

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans flex flex-col justify-between">
      {/* Top Header */}
      <header className="w-full max-w-6xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-md group-hover:bg-slate-800 transition-colors">
            <BookOpen size={20} />
          </div>
          <span className="font-serif italic font-bold text-xl text-slate-900 tracking-tight">
            Science Monitor
          </span>
        </Link>

        <div className="flex items-center gap-3">
          <div className="relative inline-flex items-center bg-white border border-slate-200 rounded-full px-2.5 py-1.5 shadow-xs hover:border-slate-300 transition-colors">
            <Globe size={14} className="text-slate-500 mr-1.5" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-hidden cursor-pointer"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>

          <div className="hidden md:flex items-center gap-2 text-xs font-semibold text-slate-600 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-xs">
            <ShieldCheck size={14} className="text-emerald-600" />
            <span>{t(language, 'login_badge_verified')}</span>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-xs transition-all active:scale-95"
          >
            <span>{t(language, 'shared_cta_try_free')}</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-3xl mx-auto px-6 py-6 md:py-12 flex-1 flex flex-col justify-center">
        {loading ? (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-md shadow-slate-100 p-12 text-center my-8">
            <div className="w-10 h-10 border-3 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm font-medium text-slate-600">
              {t(language, 'shared_loading')}
            </p>
          </div>
        ) : notFound || !article ? (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-md shadow-slate-100 p-8 sm:p-12 text-center my-8 space-y-4">
            <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-2xl flex items-center justify-center mx-auto">
              <BookOpen size={24} />
            </div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">
              {t(language, 'shared_not_found_title')}
            </h1>
            <p className="text-slate-600 text-sm sm:text-base max-w-md mx-auto">
              {t(language, 'shared_not_found_desc')}
            </p>
            <div className="pt-2">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm shadow-sm transition-all"
              >
                <span>{t(language, 'shared_back_home')}</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Main Article Card */}
            <div className="relative mx-auto w-full">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl blur-xl opacity-60 -z-10" />

              <article className="bg-white rounded-3xl border border-slate-200/90 shadow-md shadow-slate-100 overflow-hidden text-left flex flex-col">
                {/* Card Header */}
                <div className="p-6 sm:p-8 border-b border-slate-100 shrink-0">
                  {article.topic && (
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-[10px] font-bold text-blue-700 uppercase tracking-widest bg-blue-50 border border-blue-100/60 px-3 py-1 rounded-full">
                        {article.topic}
                      </span>
                    </div>
                  )}

                  <h1 className="text-xl sm:text-2xl md:text-3xl font-serif mb-4 leading-snug text-slate-900">
                    {title}
                  </h1>

                  <div className="space-y-1.5 pt-1">
                    {article.authors && (
                      <p className="text-xs sm:text-sm text-slate-600 font-medium italic flex items-start gap-2">
                        <User size={15} className="shrink-0 mt-0.5 text-slate-400" />
                        <span className="leading-relaxed">{article.authors}</span>
                      </p>
                    )}
                    {(article.venue || date) && (
                      <p className="text-[11px] sm:text-xs text-slate-400 uppercase tracking-wider font-sans">
                        {[article.venue, date].filter(Boolean).join(' • ')}
                      </p>
                    )}
                  </div>
                </div>

                {/* AI Summary Section */}
                <div className="p-6 sm:p-8 bg-slate-50/50 flex-1 flex flex-col justify-between space-y-6">
                  <div>
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-widest mb-6">
                      <Sparkles size={14} />
                      <span>{t(language, 'login_card_ai_summary')}</span>
                    </div>

                    <div className="space-y-4 text-slate-700 text-sm sm:text-base font-serif leading-relaxed">
                      {renderAISummary
                        ? renderAISummary(article.ai_summary, language)
                        : article.ai_summary}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="pt-5 border-t border-slate-200/70 flex flex-wrap justify-between items-center gap-3 shrink-0">
                    {originalUrl ? (
                      <a
                        href={originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 cursor-pointer transition-colors"
                      >
                        <span>{t(language, 'login_card_read_original')}</span>
                        <ExternalLink size={13} />
                      </a>
                    ) : (
                      <div />
                    )}
                    <span className="text-[11px] text-slate-400 font-sans tracking-wide">
                      {t(language, 'login_card_peer_reviewed')}
                    </span>
                  </div>
                </div>
              </article>
            </div>

            {/* Bottom Conversion Banner */}
            <section className="bg-white rounded-3xl border border-slate-200 shadow-lg shadow-slate-200/50 p-6 sm:p-8 md:p-10 text-center space-y-5">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide">
                <Sparkles size={14} className="text-blue-500" />
                <span>{t(language, 'login_hero_badge')}</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900 tracking-tight">
                {t(language, 'shared_banner_title')}
              </h2>

              <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
                {t(language, 'shared_banner_desc')}
              </p>

              <div className="pt-2 flex flex-col items-center space-y-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-7 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm sm:text-base shadow-sm transition-all hover:translate-y-[-1px] active:scale-[0.99]"
                >
                  <span>{t(language, 'shared_banner_cta')}</span>
                  <ArrowRight size={18} />
                </Link>

                <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    {t(language, 'login_trust_14_days')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    {t(language, 'login_trust_no_card')}
                  </span>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="w-full max-w-6xl mx-auto px-6 py-6 border-t border-slate-200/60 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
        <p>© {new Date().getFullYear()} {t(language, 'login_footer_copy')}</p>
        <div className="flex items-center gap-6">
          <Link to="/about" className="hover:text-slate-600 transition-colors font-medium">
            {t(language, 'about_title')}
          </Link>
          <Link to="/terms" className="hover:text-slate-600 transition-colors">
            {t(language, 'login_footer_terms')}
          </Link>
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">
            {t(language, 'login_footer_privacy')}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default SharedArticleView;
